import { ViewPreset } from "../view/ViewPreset";
import { AccessoryIcon } from "../view/accessories/AccessoryIcon";
import { AccessoryView } from "../view/accessories/AccessoryView";
import { ToolsView } from "../view/ToolsView";
import { PanAndZoomGestures } from "../utils/interaction/gestures/PanAndZoomGestures";
import { OrbitControls } from "./OrbitControls";
import { NodePlacer } from "./NodePlacer";
import { ContentView } from "../view/ContentView";
import { SceneNodeState } from "../scene/ISceneNode";
import { ISceneAddon } from "../scene/addon/ISceneAddon";
import { Main } from "../Main";
import { Pointer } from "../utils/interaction/Pointer";
import { IGesture } from "../utils/interaction/gestures/IGesture";
import { Vector3, Camera, PerspectiveCamera } from "three";
import { ZoomController } from "./ZoomController";
import { ConfirmWindow } from "../ui/ConfirmWindow";

export interface IViewSettings
{
	distance: {
		initial: number;
		min: number;
		max: number;
	};
	easing: {
		distance: number;
		rotation: number;
	}
}

export interface IPlacer
{
	updatePosition: (position: number[], camera: Camera) => boolean;
	placeNode(dispatch:boolean): boolean | Promise<boolean>;
	getPlacingNodeHasValidPlace(): boolean;
}

export interface IPlacing
{
	placer: IPlacer;
	accessoryIcon?: AccessoryIcon;
	//touch: boolean;
	//waitingForConfirm
}

/**
 * Manages OrbitController (which rotates, zooms the building) and
 * NodePlacer which manages placing of new nodes on the building (doors, etc).
 *
 * With mouse controls: update on mousemove, placing on mouseclick.
 * With touch: pan to drag position, add on end of drag. TODO consider adding on consequent tap?
 */
export class BuildingController
{

	private _viewConfig: IViewSettings;

	private _camera: PerspectiveCamera;
	private _element: HTMLCanvasElement;

	private _viewPreset: ViewPreset;
	private _accessoryView: AccessoryView;
	private _toolsView: ToolsView;

	private _gestures: PanAndZoomGestures;
	private _orbitControls: OrbitControls;
	private _nodePlacer: NodePlacer;

	private _rotating: boolean = false;

	private _startPinchDistance: number;

	private _placing: IPlacing;

	private _offsetFromGround: number;

	private _isAddonPositionValid: boolean;

	constructor(contentView: ContentView, tools: ToolsView)
	{
		this._viewConfig = contentView.config.config.view;
		this._camera     = contentView.camera;
		this._element    = contentView.canvas;
		this._offsetFromGround = 5;

		this._toolsView = tools;

		// gestures
		this._gestures = this.createGestures();

		// nodePlacer
		this._nodePlacer = new NodePlacer(contentView);
		contentView.scene.changeSignal.add(this.onSceneChange, {scope: this});

		// controller
		this.createController();

		// viewPreset
		this._viewPreset = new ViewPreset();
		this._viewPreset.changeSignal.add(this.onViewPresetChange);

		// accessoryView
		this.initAccessoryView(contentView);


		// miscellanious
		this._isAddonPositionValid = false;
	}

	private createGestures()
	{
		const gestures =  new PanAndZoomGestures({
			element: this._element
		});
		gestures.pan.start.add(this.onPanStart, {scope: this});
		gestures.pan.update.add(this.onPanUpdate, {scope: this});
		gestures.pan.end.add(this.onPanEnd, { scope: this });

		return gestures;
	}

	private onPanStart = (pointer: Pointer) =>
	{
		if (this._placing)
		{
			this.updatePlacingNodeByPointer(pointer);
		}

		var startRotating = !this.placingNodeHasValidPlace();
		if (startRotating)
		{
			this._rotating = true;
			//this._orbitControls.startRotation(pointer);
		}
		else
		{
			this._rotating = false;
			this.updatePlacingNodeByPointer(pointer);
		}

		Main.getInstance().render = true;
	}

	private onPanUpdate = (pointer: Pointer) =>
	{
		if (this._rotating)
		{
			//this._orbitControls.updateRotation(pointer);
		}
		else
		{
			this.updatePlacingNodeByPointer(pointer);
		}

		Main.getInstance().render = true;
	}

	private onPanEnd = async (pointer: Pointer, breakingGesture: IGesture) =>
	{
		if (this._rotating)
		{
			this._rotating = false;
			//this._orbitControls.endRotation(pointer);
		}
		else
		{
			// check if node can be added
			// only if there's no breaking gesture (pinch zoom), if there is
			// we don't want to place the item
			if (!breakingGesture)
			{
				await this.finishPlacing();
			}
		}


		// TODO consider using tap instead?

		Main.getInstance().render = true;
	}

	private initAccessoryView(contentView: ContentView)
	{
		this._accessoryView = new AccessoryView({
			accessoryConfig: contentView.config.config["accessories"],
			accessories: contentView.config.accessories,
			scene: contentView.building
		});

		this._accessoryView.moveIconClickSignal.add(this.onMoveIconClick);
	}

	private onMoveIconClick = (accessoryIcon: AccessoryIcon) =>
	{
		this.startPlacing(accessoryIcon.accessory, accessoryIcon)
	}

	private onSceneChange = async (state: SceneNodeState, node: ISceneAddon, isUserManuallyPlacing: boolean = false) =>
	{
		if (state === SceneNodeState.PLACING)
		{
			// node becomes placing, ie. the user can move the mouse and find a place for it
			this.startPlacing(this._nodePlacer);
			document.body.addEventListener("mousemove", this.onMouseHover);
		}
		else if (state === SceneNodeState.CANCELED || state === SceneNodeState.PLACED)
		{
			// node is canceled or placed -> remove listeners, cursor style

			if (state === SceneNodeState.PLACED)
			{
				// dont dispatch another PLACED event, that will cause an infinite loop
				await this.finishPlacing(false, isUserManuallyPlacing);
			}
			else
			{
				this._placing = null;
			}
			document.body.removeEventListener("mousemove", this.onMouseHover);
			window.document.body.style.cursor = "";
		}

		Main.getInstance().render = true;
	}

	private startPlacing(placer: IPlacer, accessoryIcon?: AccessoryIcon)
	{
		this._orbitControls.enableRotate = false;
		this.stopPlacingAccessoryIcon();

		this._placing = {
			placer: placer,
			accessoryIcon: accessoryIcon
		};
	}

	private stopPlacingAccessoryIcon()
	{
		if (this._placing && this._placing.accessoryIcon)
		{
			this._orbitControls.enableRotate = true;
			this._placing.accessoryIcon.stopPlacing();
		}
	}

	private createController()
	{
		this._camera.position.set(40, 34, 70);
		this._orbitControls = new OrbitControls(this._camera, this._element, this, null);
		new ZoomController(this._orbitControls);
		this._orbitControls.setTarget(0, this._offsetFromGround, 0);
		this._orbitControls.activate();
	}

	private placingNodeHasValidPlace()
	{
		if (this._placing)
		{
			return this._placing.placer.getPlacingNodeHasValidPlace();
		}
		return false;
	}


	private async finishPlacing(dispatch = true, isUserManuallyPlacing: boolean = true)
	{
		var placeSuccessful = null;

		if (this._placing)
		{
			if (this._isAddonPositionValid)
			{
				placeSuccessful = this._placing.placer.placeNode(dispatch);
			}
			else if (isUserManuallyPlacing)
			{
				const tryAgain = await ConfirmWindow.open(`Invalid placement of add-on. Please move it to a valid position. Do you want to try again?`);
				if (!tryAgain)
				{
					this._toolsView.scenePanel.cancelPlacing();
				}
			}
		}

		// example for unsuccessful placing: when placing a door but releasing on the ground
		if (placeSuccessful)
		{
			this.stopPlacingAccessoryIcon();
			this._placing = null;
		}

		Main.getInstance().render = true;
	}

	// this runs when the mouse hovers over the canvas in node placing mode
	private onMouseHover = (event: MouseEvent) =>
	{
		var localCoords = this._gestures.pointerDetector.getLocalCoords(event);
		this.updatePlacingNode(localCoords);
	};

	public lookAtBuilding(buildingID: number)
	{
		//const distance = this._camera.position.clone().sub(this._orbitControls.target.clone());
		this._orbitControls.setTarget(buildingID*Main.getInstance().distanceBetweenBuildings, this._offsetFromGround, 0);
		//this._orbitControls.setPosition(buildingID*Main.getInstance().distanceBetweenBuildings + distance.x, this._camera.position.y, this._camera.position.z);
	}

	// updates the position of the currently placing node
	private updatePlacingNode(position: number[])
	{
		// convert from [0, x] to [-1, 1]
		position[0] =   (position[0] / this._element.clientWidth)  * 2 - 1;
		position[1] = - (position[1] / this._element.clientHeight) * 2 + 1;

		if (this._placing)
		{
			this._isAddonPositionValid = this._placing.placer.updatePosition(position, this._camera);
		}

		Main.getInstance().render = true;
	}

	private updatePlacingNodeByPointer(pointer: Pointer)
	{
		this.updatePlacingNode([pointer.localX, pointer.localY]);

		Main.getInstance().render = true;
	}

	// ======================================================================================================
	// viewpreset change

	public onUserChange = () =>
	{
		this._viewPreset.value = "custom";
	};


	public onViewPresetChange = (preset:string) =>
	{
		const eye = new Vector3();
		const dist = this._viewConfig.distance.initial;
		const edgeHeight = 20;
		const edgeX = 0.25 * dist;
		const edgeZ = 0.8 * dist;

		const buildingWidth = Main.getInstance().config.building.width.value;
		const buildingLength = Main.getInstance().config.building.length.value;
		const buildingHeight = Main.getInstance().config.building.height.value;

		const ratio = Main.getInstance().isInMobileMode ? window.innerWidth / window.innerHeight : this._camera.aspect;

		const coeff = 2 / (ratio / 0.5625);

		const distanceFromRight = (buildingWidth / 2 + buildingLength*coeff);
		const distanceFromFront = (buildingLength / 2 + buildingWidth*coeff);

		switch (preset)
		{
			case "top":

				eye.set(0, dist, 0);
				break;

			case "front":

				eye.set(0, buildingHeight / 2, distanceFromFront);
				break;

			case "front-right":

				eye.set(edgeX, edgeHeight, edgeZ);
				break;

			case "right":

				eye.set(distanceFromRight, buildingHeight / 2, 0);
				break;

			case "back-right":

				eye.set(edgeX, edgeHeight, -edgeZ);
				break;

			case "back":

				eye.set(0, buildingHeight / 2, -distanceFromFront);
				break;

			case "back-left":

				eye.set(-edgeX, edgeHeight, -edgeZ);
				break;

			case "left":

				eye.set(-distanceFromRight, buildingHeight / 2, 0);
				break;

			case "front-left":

				eye.set(-edgeX, edgeHeight, edgeZ);
				break;

			case "inside":

				eye.set(0, this._offsetFromGround, -15);

				break;

			case "default":
				eye.set(40, 34, 70);

				break;
		}

		if (eye)
		{
			this._orbitControls.setPosition(eye.x, eye.y, eye.z);
		}
	};


	public update(): boolean
	{
		if (this._orbitControls)
		{
			return this._orbitControls.update();
		}

		return true;
	}

	public get accessoryView()
	{
		return this._accessoryView;
	}

	public get orbitControls(): OrbitControls
	{
		return this._orbitControls;
	}
}